<script setup lang="ts">
import { computed, onMounted } from 'vue'
import { RouterLink, RouterView, useRoute } from 'vue-router'
import { useAuthStore } from '@/stores/auth'

const route = useRoute()
const auth = useAuthStore()

// The survey renders with no chrome at all. Nothing about the practice's admin
// navigation belongs on a page a pet owner opened from a text message.
const useChrome = computed(() => {
  if (route.meta.standalone === true || route.meta.public === true) return false
  return auth.isAuthenticated
})

onMounted(async () => {
  if (auth.token && !auth.user) await auth.fetchMe()
})

const nav = [
  { to: '/', label: 'Dashboard' },
  { to: '/responses', label: 'Responses' },
  { to: '/survey', label: 'Survey' },
  { to: '/settings', label: 'Settings' },
]
</script>

<template>
  <RouterView v-if="!useChrome" />
  <div v-else class="min-h-screen bg-gray-50">
    <nav class="bg-white border-b border-gray-200">
      <div class="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 flex h-16 items-center justify-between">
        <div class="flex items-center gap-8">
          <RouterLink to="/" class="text-xl font-bold text-gray-900">VetReviews</RouterLink>
          <div class="hidden sm:flex items-center gap-1">
            <RouterLink
              v-for="item in nav"
              :key="item.to"
              :to="item.to"
              class="px-3 py-2 rounded-md text-sm text-gray-600 hover:bg-gray-50 hover:text-gray-900"
              :class="{ 'text-gray-900 font-medium bg-gray-50': route.path === item.to }"
            >
              {{ item.label }}
            </RouterLink>
          </div>
        </div>
        <div class="flex items-center gap-3">
          <span class="hidden sm:inline text-sm text-gray-500 truncate max-w-[16rem]">
            {{ auth.user?.tenant_name }}
          </span>
          <button
            type="button"
            class="px-3 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
            @click="auth.logout()"
          >
            Sign out
          </button>
        </div>
      </div>
      <div class="sm:hidden border-t border-gray-100 px-4 py-2 flex gap-1">
        <RouterLink
          v-for="item in nav"
          :key="item.to"
          :to="item.to"
          class="px-3 py-1.5 rounded-md text-sm text-gray-600"
          :class="{ 'text-gray-900 font-medium bg-gray-50': route.path === item.to }"
        >
          {{ item.label }}
        </RouterLink>
      </div>
    </nav>
    <main class="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <RouterView />
    </main>
  </div>
</template>
